import { useEffect, useState, useMemo } from "react";
import { FcAddImage, FcLike, FcManager, FcShop, FcSms } from "react-icons/fc";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from '../../Hooks/useAuth';
import { motion } from "framer-motion";
import { UserProfile } from '../../types/userTypes';
import supabase from '../../config/supabaseConfig';
import './nav.css';

const MobileNav = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [activeNav, setActiveNav] = useState<string>("home");
    const [userData, setUserData] = useState<UserProfile | null>(null);

    // Fetch supabase user data (to get custom image URL)
    useEffect(() => {
        if (user) {
            const getUserData = async () => {
                try {
                    // Don't use .single() to avoid the error when no profile exists
                    const { data, error } = await supabase
                        .from('profiles')
                        .select('*')
                        .eq('id', user.id);

                    if (error) throw error;
                    if (data && data.length > 0) {
                        setUserData(data[0] as UserProfile);
                    }
                } catch (err) {
                    console.error('[MobileNav] getUserData error:', err);
                }
            };
            getUserData();
        }
    }, [user]);

    // Determine which avatar to display: supabase imageUrl, supabase photoUrl, or Auth photoURL
    const avatarUrl = userData?.photo_url || null;

    // Profile icon should be either the image or default
    const profileIcon = avatarUrl ? (
        <img
            src={avatarUrl}
            alt="Profile"
            className="h-8 w-8 rounded-full object-cover"
        />
    ) : (
        <FcManager size={25} /> // fallback icon
    );

    // Tab items with icons and paths. Memoized so it only changes when
    // the profile icon (avatarUrl) or auth user actually changes —
    // without this, navLinks was a brand new array every render, which
    // is why it couldn't safely be added as an effect dependency below.
    const navLinks = useMemo(() => [
        { key: "home", text: "Accueil", icon: <FcShop size={25} />, path: "/" },
        { key: "favorites", text: "Favoris", icon: <FcLike size={25} />, path: "/favorites" },
        { key: "add", text: "Vendre", icon: <FcAddImage size={30} />, path: "/sell" },
        { key: "messages", text: "Messages", icon: <FcSms size={25} />, path: "/chats" },
        { key: "profile", text: "Profil", icon: profileIcon, path: user ? `/profile/${user.id}` : "/auth/login" },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [avatarUrl, user]);

    // Update active tab based on current location
    useEffect(() => {
        const currentPath = location.pathname;
        const matching = navLinks.find(link =>
            currentPath === link.path ||
            (link.key === "profile" && currentPath.startsWith("/profile/"))
        );

        if (matching) {
            setActiveNav(matching.key);
        }
    }, [location.pathname, navLinks]);

    const handleNavClick = (key: string) => {
        const selected = navLinks.find(link => link.key === key);
        if (selected) {
            setActiveNav(key);
            setTimeout(() => navigate(selected.path), 10);
        }
    };

    return (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg z-30 border-t border-gray-200">
            <div className="flex justify-around items-center h-16 px-2 max-w-7xl mx-auto">
                {navLinks.map(link => (
                    <motion.div
                        key={link.key}
                        className={`flex flex-col items-center justify-center w-full h-full cursor-pointer relative ${activeNav === link.key ? "text-primary-default" : "text-gray-500"
                            }`}
                        onClick={() => handleNavClick(link.key)}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.1 }}
                    >
                        <div className="relative">
                            {link.icon}
                            {link.key === "add" && (
                                <motion.div
                                    className="absolute inset-0 bg-primary-default/10 rounded-full -m-2"
                                    animate={{ scale: [1, 1.1, 1] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                />
                            )}
                        </div>
                        <span className="text-xs font-medium mt-1">{link.text}</span>

                        {activeNav === link.key && (
                            <motion.div
                                className="absolute bottom-0 w-1/2 h-1 bg-primary-default rounded-t-full"
                                layoutId="activeTab"
                                initial={false}
                            />
                        )}
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

export default MobileNav;
